import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ShellClientService } from '../shell-communication/shell-client.service';
import { SessionService } from '../authentication/session-service.service';
import { LocationService } from '../location.service';
import { LoggerService } from '../logging/logger-factory.service';
import { Logger } from '../logging/logger.interface';
import { MessageBusService } from '../message-bus.service';
import { AuthTokenExpiredUIMessage } from '../ui-messages/auth-token-expired-ui-message.interface';
import { GetManagedClientsSettingsByKeyPrefix } from '../shell-communication/shell-operations-protocol';
import { UserSettingDto } from '../shell-communication/shell-dto-protocol';
import { ShellMessagesService } from '../shell-messages/shell-messages.service';

declare function setInterval(callback: () => void, timeout: number): number;

/**
 * CR-20260424-002 — Cross-server client data change notification (STAGE only).
 *
 * On STAGE, periodically polls the backend for managed clients' user-settings
 * matching the HG/APG prefix set, compares to an isolated in-memory snapshot,
 * and fires a toast on drift.
 *
 * This service is deliberately isolated from the existing same-server flow:
 *   - Does NOT touch `_theirSnapshotState` / `_theirCurrentState` in
 *     `user-settings.service.ts`.
 *   - Does NOT subscribe to `UserSettingsChangedDto` (same-server fast-path).
 *   - Emits ONLY toasts; no refresh-button pulse, no auto-reload. User
 *     relogins manually to reset webtrader state.
 *
 * Host-gating: permanently inert unless `LocationService.isStagingServer`.
 *
 * Prefix set mirrors the same-server sync-button trigger set exactly, per
 * `UserSettingsChangedDto` → service → relay-topic trace in
 * `HgOriginalClientPositionsService` + `ApgOriginalClientPositionsService`.
 * See CR-20260424-002 requirements-spec.md FR-8 and ADR-006.
 */
@Injectable({ providedIn: 'root' })
export class CrossServerSettingsDriftPoller {

    private static readonly POLL_INTERVAL_MS = 10000;

    private static readonly PREFIXES: ReadonlyArray<string> = [
        'hg.hedge-positions.',
        'apg.saved-positions.',
        'apg.last-used-template.',
    ];

    private static readonly CATEGORY_BY_PREFIX: ReadonlyArray<{ prefix: string, label: 'Hedging Grid' | 'Adjustment Pricing Grid' }> = [
        { prefix: 'hg.hedge-positions.',    label: 'Hedging Grid' },
        { prefix: 'apg.saved-positions.',   label: 'Adjustment Pricing Grid' },
        { prefix: 'apg.last-used-template.', label: 'Adjustment Pricing Grid' },
    ];

    // Isolated snapshot: userId -> (key -> JSON-encoded value string).
    // Deliberately NOT shared with UserSettingsService internal state.
    private readonly _snapshot = new Map<string, Map<string, string>>();

    // Per-toast dedupe: "userId|label" -> lastToastAtMs. Bounds toast count
    // to at most 1 per (client, category) per 3s sliding window (NFR-6).
    private static readonly AGGREGATION_WINDOW_MS = 3000;
    private readonly _lastToastAt = new Map<string, number>();

    private _pollingWorkerRef: number | null = null;
    private _started = false;
    private readonly _logger: Logger;

    constructor(
        private readonly _shellClient: ShellClientService,
        private readonly _sessionService: SessionService,
        private readonly _locationService: LocationService,
        private readonly _toastr: ToastrService,
        private readonly _messageBus: MessageBusService,
        private readonly _shellMessages: ShellMessagesService,
        loggerService: LoggerService,
    ) {
        this._logger = loggerService.createLogger('CrossServerSettingsDriftPoller');
    }

    /**
     * Start the poller. No-op unless running on STAGE host.
     * Safe to call multiple times (idempotent).
     */
    async start(): Promise<void> {
        if (this._started) {
            return;
        }

        if (!this._locationService.isStagingServer) {
            this._logger.info('Not STAGE host — poller inert.');
            this._started = true;
            return;
        }

        if (!this._sessionService.isAuthenticated) {
            this._logger.warn('Not authenticated — poller deferred.');
            return;
        }

        this._started = true;

        // Seed snapshot from first poll (snapshot = baseline, not drift signal).
        const initialRows = await this._fetch();
        for (const row of initialRows) {
            this._putSnapshot(row.userId, row.key, row.value);
        }

        this._logger.info(`Snapshot seeded: ${initialRows.length} row(s). Starting poll loop (${CrossServerSettingsDriftPoller.POLL_INTERVAL_MS}ms interval).`);

        this._pollingWorkerRef = setInterval(
            () => this._pollOnce(),
            CrossServerSettingsDriftPoller.POLL_INTERVAL_MS,
        );

        this._messageBus
            .of<AuthTokenExpiredUIMessage>('AuthTokenExpiredUIMessage')
            .subscribe(() => this.stop());
    }

    /**
     * Stop the poller and clear snapshot + dedupe state.
     * Safe to call even if not started.
     */
    stop(): void {
        if (this._pollingWorkerRef !== null) {
            clearInterval(this._pollingWorkerRef);
            this._pollingWorkerRef = null;
        }
        this._snapshot.clear();
        this._lastToastAt.clear();
        this._started = false;
        // Dismiss any drift toast already visible at logout moment. No-arg
        // clear() = dismiss all (ngx-toastr API). User intent: "clear all"
        // (CR-20260515-004 AC-2).
        this._toastr.clear();
        this._logger.info('Poller stopped.');
    }

    private async _pollOnce(): Promise<void> {
        if (!this._sessionService.isAuthenticated) {
            return;
        }

        let rows: UserSettingDto[];
        try {
            rows = await this._fetch();
        } catch (e) {
            this._logger.warn(`Poll query failed: ${e && e.message ? e.message : e}`);
            return;
        }

        // Logout landed during the await — drop result, do not emit
        // (CR-20260515-004 AC-3/AC-4).
        if (!this._sessionService.isAuthenticated) {
            return;
        }

        // Group drift by (userId, label) to produce at most one toast per pair per poll.
        const driftByUserCategory = new Map<string, Set<string>>(); // "userId|label" -> set of drifted keys

        for (const row of rows) {
            const prior = this._getSnapshot(row.userId, row.key);
            if (prior === row.value) {
                continue;
            }

            const label = this._categoryLabel(row.key);
            if (label === null) {
                // Out-of-scope key returned by server — shouldn't happen with server-side filter,
                // but defensive drop keeps false-positive toasts from leaking.
                continue;
            }

            // Advance snapshot immediately (idempotent dedupe across future polls).
            this._putSnapshot(row.userId, row.key, row.value);

            const groupKey = `${row.userId}|${label}`;
            let keys = driftByUserCategory.get(groupKey);
            if (!keys) {
                keys = new Set<string>();
                driftByUserCategory.set(groupKey, keys);
            }
            keys.add(row.key);
        }

        // Emit at most one toast per (userId, label), subject to aggregation-window dedupe.
        // Use forEach (not for..of on iterator) per feedback_es5_iteration.md — ES5 target.
        const now = Date.now();
        driftByUserCategory.forEach((keys, groupKey) => {
            // Logout landed mid-iteration — suppress both _toastr.info and
            // _shellMessages.addLocalMessage for this group
            // (CR-20260515-004 AC-1/AC-3).
            if (!this._sessionService.isAuthenticated) {
                return;
            }
            const last = this._lastToastAt.get(groupKey) || 0;
            if (now - last < CrossServerSettingsDriftPoller.AGGREGATION_WINDOW_MS) {
                this._logger.info(`Drift suppressed (aggregation window): ${groupKey}`);
                return;
            }
            this._lastToastAt.set(groupKey, now);

            const sepIndex = groupKey.indexOf('|');
            const userId = groupKey.substring(0, sepIndex);
            const label = groupKey.substring(sepIndex + 1);
            const clientName = this._resolveClientName(userId);
            const message = `${clientName} made changes to his ${label}. Re-login to bring-up fresh data`;
            const title = 'Client data changed';

            // Sticky toast — must be closed explicitly. User asked manager not
            // miss notification when distracted (CR-20260424-002 follow-up).
            // Position uses app default (top-center) — do not override.
            this._toastr.info(message, title, {
                disableTimeOut: true,
                closeButton: true,
                tapToDismiss: false,
            });

            // Add to session messages repository: badge + history.
            // Frontend-synthetic; never round-trips to backend.
            const shell = this._sessionService.connectedShell;
            this._shellMessages.addLocalMessage({
                timestamp: new Date(),
                level: 'Info',
                domain: 'CrossServer',
                source: 'Client data sync',
                message,
                isRead: false,
                shellId: shell ? shell.shellId : undefined,
                clientId: shell ? shell.clientId : undefined,
                shellName: shell ? shell.displayName : undefined,
                clientName,
            });

            this._logger.info(`Toast fired (sticky) + repo entry added: ${message} (${keys.size} key(s) drifted)`);
        });
    }

    private async _fetch(): Promise<UserSettingDto[]> {
        const shell = this._sessionService.connectedShell;
        if (!shell) {
            return [];
        }

        const query = new GetManagedClientsSettingsByKeyPrefix(
            CrossServerSettingsDriftPoller.PREFIXES.slice() // pass a copy
        );

        const result = await this._shellClient.processQuery<UserSettingDto[]>(query, shell.shellId);
        return result || [];
    }

    private _getSnapshot(userId: string, key: string): string | undefined {
        const byKey = this._snapshot.get(userId);
        return byKey ? byKey.get(key) : undefined;
    }

    private _putSnapshot(userId: string, key: string, value: string): void {
        let byKey = this._snapshot.get(userId);
        if (!byKey) {
            byKey = new Map<string, string>();
            this._snapshot.set(userId, byKey);
        }
        byKey.set(key, value);
    }

    private _categoryLabel(key: string): 'Hedging Grid' | 'Adjustment Pricing Grid' | null {
        for (const entry of CrossServerSettingsDriftPoller.CATEGORY_BY_PREFIX) {
            if (key.indexOf(entry.prefix) === 0) {
                return entry.label;
            }
        }
        return null;
    }

    private _resolveClientName(userId: string): string {
        const sessionData = this._sessionService.sessionData;
        if (sessionData && sessionData.users) {
            const match = sessionData.users.find(u => u.userId === userId);
            if (match) {
                return match.userName;
            }
        }
        return userId;
    }
}
